import { useReducer, useEffect } from 'react'
import {
    Box,
    Button,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    Typography,
} from '@mui/material'
import { LicenseInfo } from '@mui/x-license-pro'
import {
    useGridApiRef,
    Toolbar,
    ToolbarButton,
    GridColDef,
    GridPaginationModel,
} from '@mui/x-data-grid-pro'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { Grid } from '.'
import {
    DensityLarge,
    DensityMedium,
    DensitySmall,
    Save as SaveIcon,
    Search as SearchIcon,
    CheckCircle,
} from '@mui/icons-material'

LicenseInfo.setLicenseKey(
    '78289722ce54bd59055e8887bc9a39daTz0xMjcwMzksRT0xODA1MzI3OTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI='
)

const reducer = (state, newState) => ({ ...state, ...newState })

interface pinnedColumnsProps {
    left: string[]
    right: string[]
}

interface Props {
    data: any
    visibleColumns: any
    getRowData?: (data) => void
    columnWidths?: {
        [key: string]: number
    }
    getColumnHeaderVisibility?: (data) => void
    columnsAvailable?: any
    onRowSelectionModelChange?: any
    checkboxSelection?: boolean
    customColumnNames?: string[]
    pinnedColumns?: pinnedColumnsProps
    onColumnOrderChange?: any
    onUpdatedColumnsChange?: (onColumnOrderChange: any) => void
    columns?: any
    apiColumns?: any
    savePreferences?: any
    columnVisibilityModel?: any
    apiDensity?: any
    onDensityChange?: (density: string) => void
    sx?: {}
    rowCount?: number
    loading?: boolean
    paginationModel?: GridPaginationModel
    setPaginationModel?: (model: GridPaginationModel) => void
    paginationMode?: 'client' | 'server'
    pageSizeOptions?: any
    onApiSearch?: (searchText: any) => void
    quickSearchFields?: string[]
    pinnedRows?: any
    sortingMode?: 'client' | 'server'
    filterMode?: 'client' | 'server'
    onFilterModelChange?: (model: any) => void
}

const UniversalDataTable = ({
    data,
    visibleColumns,
    getRowData = (row) => row,
    columnWidths,
    getColumnHeaderVisibility = (column) => column,
    columnsAvailable,
    onRowSelectionModelChange,
    checkboxSelection = true,
    customColumnNames = [],
    pinnedColumns = { left: [], right: [] },
    onColumnOrderChange = () => {},
    onUpdatedColumnsChange,
    apiColumns,
    savePreferences,
    columnVisibilityModel,
    apiDensity,
    onDensityChange,
    sx,
    rowCount: propRowCount,
    loading,
    paginationModel,
    setPaginationModel,
    paginationMode = 'client',
    pageSizeOptions,
    quickSearchFields,
    onApiSearch,
    pinnedRows,
    sortingMode,
    filterMode,
    onFilterModelChange,
}: Props) => {
    const defaultState = {
        columns: [],
        rows: [],
        newColumns: apiColumns,
        defaultDensity: apiDensity || 'standard',
        anchorEl: null,
        densityIcon:
            apiDensity === 'standard' ? (
                <DensityMedium />
            ) : apiDensity === 'compact' ? (
                <DensitySmall />
            ) : apiDensity === 'comfortable' ? (
                <DensityLarge />
            ) : (
                <DensityMedium />
            ),
    }
    const [state, setState] = useReducer(reducer, defaultState)
    const { rows, columns, newColumns, defaultDensity, anchorEl, densityIcon } =
        state

    const apiRef: any = useGridApiRef()

    useEffect(() => {
        if (data && data.length !== 0) {
            let filteredColumns: any

            if (visibleColumns && visibleColumns.length !== 0) {
                filteredColumns = Object?.keys(data[0])
                    .map((key: any) => {
                        return {
                            field: key,
                            headerName: key?.toUpperCase(),
                            width: columnWidths ? columnWidths[key] : 150,
                            type:
                                key !== 'engineCalibrationActive'
                                    ? 'string'
                                    : 'boolean',
                        }
                    })
                    .filter(
                        (key) => visibleColumns.includes(key.field) && key.field
                    )

                const filteredRows = data?.map((values) => ({
                    ...values,
                    id: values?.id,
                }))

                if (customColumnNames && customColumnNames.length !== 0) {
                    filteredColumns = filteredColumns.map((column, index) => ({
                        ...column,
                        headerName: customColumnNames[index].toUpperCase(),
                    }))
                }

                return setState({
                    columns: filteredColumns,
                    rows: filteredRows,
                })
            }

            const pageData = apiColumns
            if (apiColumns && apiColumns?.length !== 0) {
                filteredColumns = Object?.keys(data[0])
                    ?.map((key) => {
                        pageData?.find((col) => col.field === key)

                        return {
                            field: key,
                            headerName: pageData.headerName,
                            width: pageData.width,
                            isVisible: pageData.isVisible,
                        }
                    })
                    .filter(({ field }) =>
                        pageData.some((col) => col.field === field)
                    )

                const filteredRows = data?.map((values) => ({
                    ...values,
                    id: values?.id,
                }))

                if (pageData && pageData.length !== 0) {
                    filteredColumns = pageData?.map((column) => ({
                        ...column,
                        headerName: column.headerName,
                    }))
                }

                return setState({
                    ...state,
                    columns: filteredColumns,
                    rows: filteredRows,
                })
            }
        } else if (visibleColumns.length === 0) {
            let filteredColumns = Object?.keys(data[0])
                ?.map((key) => ({
                    field: key,
                    headerName: key?.toUpperCase(),
                    width: columnWidths ? columnWidths[key] : 150,
                }))
                .filter(({ field }) => visibleColumns.includes(field) && field)

            const filteredRows = data?.map((values) => ({
                ...values,
                key: values?.id,
            }))

            if (customColumnNames && customColumnNames.length !== 0) {
                filteredColumns = filteredColumns.map((column, index) => ({
                    ...column,
                    headerName: customColumnNames[index].toUpperCase(),
                }))
            }

            return setState({
                columns: filteredColumns,
                rows: filteredRows,
            })
        }
    }, [data, visibleColumns, columnsAvailable])

    const handleColumnOrderChange = (params, type) => {
        if (onUpdatedColumnsChange) {
            let updatedColumns = [...newColumns]

            if (type === 'resize') {
                const { field, headerName, width } = params.colDef
                updatedColumns =
                    apiRef &&
                    apiRef?.current
                        .getAllColumns()
                        .map((col) =>
                            col.field === field
                                ? { ...col, width, headerName }
                                : col
                        )
            } else if (type === 'order') {
                updatedColumns =
                    apiRef &&
                    apiRef?.current
                        .getAllColumns()
                        .map((col: any) => ({
                            field: col.field,
                            headerName: col.headerName,
                            width: col.width,
                            isVisible: col.isVisible,
                            type: col.type,
                        }))
                        .filter((col: any) => col.field !== '__check__')
            }

            setState({ ...state, newColumns: updatedColumns })
            onUpdatedColumnsChange(updatedColumns)
        }
    }

    const search = () => {
        apiRef?.current?.showFilterPanel?.()
    }

    useEffect(() => {
        if (!apiRef.current) return

        const unsubscribe = apiRef.current.subscribeEvent(
            'filterModelChange',
            (filterModel: any) => {
                if (!filterModel?.items || filterModel.items.length === 0) {
                    onApiSearch?.(null)
                    return
                }

                const item = filterModel.items[0]

                if (item?.value) {
                    const query = {
                        columnField: item.field,
                        operator: item.operator,
                        value: item.value,
                    }
                    onApiSearch?.(query)
                } else {
                    onApiSearch?.(null)
                }
            }
        )

        return () => unsubscribe?.()
    }, [apiRef.current])

    const handleClick = (event: any) => {
        const button = event.currentTarget
        const rect = button.getBoundingClientRect(event)
        setState({
            anchorEl: {
                top: rect.bottom,
                left: rect.left,
                width: event.currentTarget.offsetWidth,
                height: event.currentTarget.offsetHeight,
            },
        })
    }

    const handleClose = () => {
        setState({ anchorEl: null })
    }

    const handleDensityChange = (newDensity) => {
        setState({ defaultDensity: newDensity })
        if (apiRef && apiRef?.currentapiRef && apiRef?.currentapiRef?.current) {
            apiRef?.currentapiRef?.current?.setDensity(newDensity)
        }
        if (newDensity === 'comfortable') {
            setState({ densityIcon: <DensityLarge /> })
        }
        if (newDensity === 'standard') {
            setState({ densityIcon: <DensityMedium /> })
        }
        if (newDensity === 'compact') {
            setState({ densityIcon: <DensitySmall /> })
        }
        handleClose()

        if (onDensityChange) {
            onDensityChange(newDensity)
        }
    }

    const open = Boolean(anchorEl)

    const CustomToolbar = () => {
        return (
            <Toolbar
            // style={{
            //     display: 'flex',
            //     justifyContent: 'flex-start',

            //     padding: 3,
            //     gap: 1,
            // }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        height: '100%',
                        gap: 0,
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'row-reverse',
                        }}
                    >
                        <Button
                            startIcon={<SaveIcon />}
                            onClick={savePreferences}
                            disabled={savePreferences ? false : true}
                            sx={{ color: 'primary' }}
                        >
                            Save Table Preferences
                        </Button>

                        <Button
                            onClick={search}
                            title="Search"
                            sx={{ color: 'primary' }}
                            startIcon={<SearchIcon />}
                        >
                            Search
                        </Button>
                        <Button
                            id="density-button"
                            aria-controls={open ? 'density-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleClick}
                            startIcon={densityIcon}
                        >
                            Density
                        </Button>
                        <Menu
                            id="density-menu"
                            anchorEl={anchorEl ? () => document.body : null}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: anchorEl?.top || 'center',
                                horizontal: anchorEl?.left || 'center',
                            }}
                            transformOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            sx={{
                                marginTop: 15,
                                marginLeft: 8,
                            }}
                        >
                            <MenuItem
                                onClick={() => handleDensityChange('compact')}
                            >
                                <ListItemIcon>
                                    <DensitySmall />
                                </ListItemIcon>
                                <ListItemText>Compact</ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleDensityChange('standard')}
                            >
                                <ListItemIcon>
                                    <DensityMedium />
                                </ListItemIcon>
                                <ListItemText>Standard</ListItemText>
                            </MenuItem>

                            <MenuItem
                                onClick={() =>
                                    handleDensityChange('comfortable')
                                }
                            >
                                <ListItemIcon>
                                    <DensityLarge />
                                </ListItemIcon>
                                <ListItemText>Comfortable</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
            </Toolbar>
        )
    }

    const isServerPagination =
        paginationMode === 'server' &&
        propRowCount !== undefined &&
        paginationModel !== undefined &&
        setPaginationModel !== undefined

    const finalRowCount = isServerPagination ? propRowCount : rows.length

    return (
        <Grid container>
            <DataGridPro
                apiRef={apiRef ? apiRef : null}
                columns={columns}
                rows={rows}
                onRowSelectionModelChange={onRowSelectionModelChange}
                pagination
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 50,
                        },
                    },
                }}
                onCellClick={({ field, row }) => {
                    if (field !== '__check__') {
                        getRowData(row)
                    }
                }}
                onColumnVisibilityModelChange={(e) =>
                    getColumnHeaderVisibility(e)
                }
                pageSizeOptions={
                    pageSizeOptions
                        ? [Number(pageSizeOptions)]
                        : [10, 25, 50, 100]
                }
                checkboxSelection={checkboxSelection}
                disableRowSelectionOnClick
                pinnedColumns={pinnedColumns}
                onColumnOrderChange={(params) =>
                    handleColumnOrderChange(params, 'order')
                }
                onColumnResize={(params) =>
                    handleColumnOrderChange(params, 'resize')
                }
                disableColumnReorder={false}
                columnVisibilityModel={columnVisibilityModel}
                slots={{
                    toolbar: CustomToolbar,
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                    },
                }}
                showToolbar={true}
                disableColumnMenu={true}
                rowCount={finalRowCount}
                loading={loading}
                paginationModel={paginationModel}
                paginationMode={paginationMode}
                onPaginationModelChange={setPaginationModel}
                density={defaultDensity}
                pinnedRows={pinnedRows}
                sx={{
                    '& .MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                        background: `linear-gradient(
                                104deg,
                                rgba(83, 110, 47, 1) 75%,
                                rgba(86, 114, 48, 1) 80%,
                                rgba(91, 122, 49, 1) 85%,
                                rgba(100, 137, 52, 1) 90%,
                                rgba(113, 157, 56, 1) 95%,
                                rgba(140, 198, 63, 0.75) 100%
                            )`,
                    },
                    border: 'none',
                    background: 'transparent',
                }}
                sortingMode={sortingMode}
                filterMode={filterMode}
                onFilterModelChange={onFilterModelChange}
            />
        </Grid>
    )
}

export default UniversalDataTable
